import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Minus, Diamond, Zap, Loader2, Flag, X } from "lucide-react";
import { ENCHANTABLE_SLOTS } from "@/lib/constants";
import { resolveItems } from "@/lib/api";
import { ilvlColor } from "@/lib/gearScore";

const WOWHEAD_BASE = "https://www.wowhead.com/tbc";

function refreshWowheadTooltips() {
  try {
    if (window.$WowheadPower && window.$WowheadPower.refreshLinks) {
      window.$WowheadPower.refreshLinks();
    }
  } catch (e) { /* silent */ }
}

function buildItemUrl(itemId, enchantId, gemIds) {
  let url = `${WOWHEAD_BASE}/item=${itemId}`;
  const params = [];
  if (gemIds && gemIds.length > 0 && gemIds.some(g => g > 0)) {
    params.push(`gems=${gemIds.map(g => g || 0).join(":")}`);
  }
  if (enchantId && enchantId > 0) {
    params.push(`ench=${enchantId}`);
  }
  if (params.length > 0) url += "&" + params.join("&");
  return url;
}

function EnchantBadge({ hasEnchant, slot, enchantId, enchantName, itemId }) {
  const isEnchantable = ENCHANTABLE_SLOTS.includes(slot);
  if (!isEnchantable) return <span className="text-[11px] text-muted-foreground">N/A</span>;

  const icon = hasEnchant === true
    ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
    : hasEnchant === false
      ? <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
      : <Minus className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />;

  if (hasEnchant && itemId > 0 && enchantId > 0) {
    return (
      <a href={`${WOWHEAD_BASE}/item=${itemId}&ench=${enchantId}`}
        onClick={(e) => e.preventDefault()}
        className="flex items-center gap-1 cursor-pointer group/ench">
        {icon}
        <span className="text-[10px] truncate max-w-[80px] text-green-400 group-hover/ench:underline">
          {enchantName || `#${enchantId}`}
        </span>
      </a>
    );
  }
  return (
    <div className="flex items-center gap-1" title={hasEnchant === false ? "No Enchant" : "Unknown"}>
      {icon}
      <span className={`text-[10px] truncate max-w-[80px] ${hasEnchant === false ? "text-red-400" : "text-muted-foreground"}`}>
        {hasEnchant === false ? "Missing" : ""}
      </span>
    </div>
  );
}

function GemDots({ gemIds, gemInfos }) {
  const filled = gemIds?.filter(g => g > 0).length || 0;
  const count = gemIds?.length || 0;
  if (count === 0 && filled === 0) return <span className="text-[11px] text-muted-foreground">-</span>;
  return (
    <div className="flex gap-1 items-center">
      {gemIds.map((gid, i) => {
        const info = gemInfos?.[i];
        if (gid > 0) {
          return (
            <a key={i} href={`${WOWHEAD_BASE}/item=${gid}`} onClick={(e) => e.preventDefault()} className="inline-flex">
              {info?.icon ? (
                <img src={info.icon} alt={info.name || ""} className="w-4 h-4 rounded-sm border border-border/50 hover:border-purple-400 transition-colors" loading="lazy" />
              ) : (
                <div className="w-4 h-4 rounded-sm bg-green-500/30 border border-green-500/50" />
              )}
            </a>
          );
        }
        return <div key={i} className="w-4 h-4 rounded-sm bg-muted/30 border border-border/30" />;
      })}
      {filled < count && <span className="text-[10px] text-red-400">{count - filled}</span>}
    </div>
  );
}

function ILvlBadge({ level }) {
  if (!level) return null;
  const c = ilvlColor(level);
  return (
    <span
      className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded-sm border"
      style={{ color: c.hex, borderColor: `${c.hex}55`, backgroundColor: `${c.hex}15` }}
      title={c.label}
    >
      {level}
    </span>
  );
}

export default function GearTab({ data, upgradeFlags, onFlag, onUnflag }) {
  const gear = data?.gear || [];
  const [resolvedData, setResolvedData] = useState({ items: {}, enchants: {}, gems: {} });
  const [loading, setLoading] = useState(false);
  const [flagMenu, setFlagMenu] = useState(null); // index of slot showing flag menu

  const triggerWowhead = useCallback(() => {
    setTimeout(refreshWowheadTooltips, 300);
  }, []);

  useEffect(() => {
    const gearData = data?.gear || [];
    const itemIds = gearData.map((g) => g.item_id).filter(Boolean);
    const enchantIds = gearData.map((g) => g.enchant_id).filter(Boolean);
    const gemIds = gearData.flatMap((g) => g.gem_ids || []).filter(Boolean);
    if (itemIds.length === 0) return;
    setLoading(true);
    resolveItems(itemIds, enchantIds, gemIds)
      .then((res) => {
        setResolvedData(res);
        setTimeout(refreshWowheadTooltips, 500);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [data]);

  useEffect(() => { triggerWowhead(); }, [resolvedData, triggerWowhead]);

  const getItemInfo = (itemId) => resolvedData.items?.[String(itemId)];
  const getEnchantName = (enchantId) => resolvedData.enchants?.[String(enchantId)]?.name;
  const getGemInfo = (gemIds) =>
    (gemIds || []).map((gid) => {
      const info = resolvedData.gems?.[String(gid)];
      return info ? { id: gid, name: info.name, icon: info.icon, nameColor: info.color } : { id: gid, name: `Gem #${gid}`, icon: null, nameColor: "#1eff00" };
    });

  return (
    <div className="mt-4" data-testid="gear-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight">Gear Progression</h3>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground inline ml-2" />}
        </div>
      </div>

      <div className="space-y-0">
        <div className="grid grid-cols-[110px_28px_1fr_30px_50px_80px_36px] gap-2 px-2 py-1.5 border-b border-border">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Slot</span>
          <span />
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Item</span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">iLvl</span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-0.5"><Zap className="h-3 w-3" /></span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-0.5"><Diamond className="h-3 w-3" /> Gems</span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center"><Flag className="h-3 w-3" /></span>
        </div>

        {gear.map((gs, index) => {
          const hasItem = gs.item_id > 0;
          const itemInfo = getItemInfo(gs.item_id);
          const enchantName = getEnchantName(gs.enchant_id);
          const gemInfos = getGemInfo(gs.gem_ids);
          const isEnchantable = ENCHANTABLE_SLOTS.includes(gs.slot);
          const missingEnchant = isEnchantable && hasItem && gs.has_enchant === false;
          const itemUrl = hasItem ? buildItemUrl(gs.item_id, gs.enchant_id, gs.gem_ids) : "#";
          const slotFlags = (upgradeFlags || []).filter(f => f.slot === gs.slot);
          const showFlagMenu = flagMenu === index;

          return (
            <div key={gs.slot} data-testid={`gear-slot-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
              className={`relative grid grid-cols-[110px_28px_1fr_30px_50px_80px_36px] gap-2 px-2 py-1 items-center hover:bg-card/50 transition-colors border-b border-border/20 group ${missingEnchant ? "bg-destructive/5" : ""}`}>
              
              <span className="text-[13px] font-medium text-foreground/70">{gs.slot}</span>

              <div className="flex items-center justify-center">
                {hasItem ? (
                  <a href={itemUrl} onClick={(e) => e.preventDefault()} className="inline-flex">
                    {itemInfo?.icon ? (
                      <img src={itemInfo.icon} alt="" className="w-6 h-6 rounded-[3px] border border-border/50 hover:brightness-125 transition-all" style={{ borderColor: itemInfo.color || "#333" }} loading="lazy" />
                    ) : (
                      <div className="w-6 h-6 rounded-[3px] bg-secondary/50 border border-border/50" />
                    )}
                  </a>
                ) : null}
              </div>

              {hasItem ? (
                <a href={itemUrl} onClick={(e) => e.preventDefault()}
                  className="text-[13px] hover:underline truncate transition-colors"
                  style={{ color: itemInfo?.color || "#0070dd" }}>
                  {itemInfo?.name || gs.item_name || `Item #${gs.item_id}`}
                </a>
              ) : (
                <span className="text-[13px] text-muted-foreground/50">Empty</span>
              )}

              <ILvlBadge level={gs.item_level} />

              <EnchantBadge hasEnchant={gs.has_enchant} slot={gs.slot} enchantId={gs.enchant_id} enchantName={enchantName} itemId={gs.item_id} />

              <GemDots gemIds={gs.gem_ids || []} gemInfos={gemInfos} />

              {/* Flag column */}
              {hasItem && onFlag ? (
                <div className="relative">
                  {slotFlags.length > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      {slotFlags.map((f) => (
                        <button key={f.id}
                          data-testid={`unflag-${f.flag_type}-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                          onClick={() => onUnflag(f.id)}
                          title={`Remove ${f.flag_type} flag: ${f.flagged_name}`}
                          className="flex items-center justify-center h-5 w-full rounded-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-[8px] font-mono uppercase">
                          <X className="h-2.5 w-2.5 mr-0.5" />{f.flag_type.charAt(0)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button
                      data-testid={`flag-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                      onClick={() => setFlagMenu(showFlagMenu ? null : index)}
                      title="Flag for upgrade"
                      className="flex items-center justify-center h-6 w-6 rounded-sm text-muted-foreground/40 hover:text-orange-400 hover:bg-orange-500/10 transition-colors opacity-0 group-hover:opacity-100">
                      <Flag className="h-3 w-3" />
                    </button>
                  )}

                  {/* Flag type dropdown */}
                  {showFlagMenu && (
                    <div className="absolute right-0 top-7 z-50 bg-background border border-border/40 rounded-sm shadow-lg py-1 min-w-[130px]"
                      data-testid={`flag-menu-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}>
                      <button
                        data-testid={`flag-item-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                        onClick={() => { onFlag(gs.slot, "item", gs.item_id, itemInfo?.name || gs.item_name || `Item #${gs.item_id}`); setFlagMenu(null); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-card/50 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-400" /> Item
                      </button>
                      {isEnchantable && gs.enchant_id > 0 && (
                        <button
                          data-testid={`flag-enchant-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                          onClick={() => { onFlag(gs.slot, "enchant", gs.enchant_id, enchantName || `Enchant #${gs.enchant_id}`); setFlagMenu(null); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-card/50 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-400" /> Enchant
                        </button>
                      )}
                      {isEnchantable && (!gs.enchant_id || gs.has_enchant === false) && (
                        <button
                          data-testid={`flag-enchant-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                          onClick={() => { onFlag(gs.slot, "enchant", 0, "Missing enchant"); setFlagMenu(null); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-card/50 flex items-center gap-2 text-red-400">
                          <span className="w-2 h-2 rounded-full bg-red-400" /> Missing Enchant
                        </button>
                      )}
                      {(gs.gem_ids || []).map((gid, gi) => {
                        if (gid > 0) {
                          const gInfo = gemInfos[gi];
                          return (
                            <button key={gi}
                              data-testid={`flag-gem-${gi}-${gs.slot.toLowerCase().replace(/\s|\//g, "-")}`}
                              onClick={() => { onFlag(gs.slot, "gem", gid, gInfo?.name || `Gem #${gid}`); setFlagMenu(null); }}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-card/50 flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-400" /> Gem {gi + 1}
                            </button>
                          );
                        }
                        return (
                          <button key={gi}
                            onClick={() => { onFlag(gs.slot, "gem", 0, `Empty gem slot ${gi + 1}`); setFlagMenu(null); }}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-card/50 flex items-center gap-2 text-red-400">
                            <span className="w-2 h-2 rounded-full bg-red-400" /> Empty Gem {gi + 1}
                          </button>
                        );
                      })}
                      <div className="border-t border-border/20 mt-1 pt-1">
                        <button onClick={() => setFlagMenu(null)}
                          className="w-full text-left px-3 py-1 text-[10px] text-muted-foreground hover:bg-card/50">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : <span />}
            </div>
          );
        })}
      </div>

      {/* Summary row */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCard label="Enchants"
          good={gear.filter((g) => g.has_enchant === true && ENCHANTABLE_SLOTS.includes(g.slot)).length}
          total={gear.filter((g) => ENCHANTABLE_SLOTS.includes(g.slot) && g.item_id > 0).length}
        />
        <SummaryCard label="Gems"
          good={gear.reduce((s, g) => s + (g.gems_filled || 0), 0)}
          total={gear.reduce((s, g) => s + (g.gem_count || 0), 0)}
        />
        <div className="stat-card">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Avg iLvl</span>
          <p className="text-base font-bold font-mono text-blue-400">
            {(() => { const items = gear.filter((g) => g.item_level > 0); return items.length ? Math.round(items.reduce((s, g) => s + g.item_level, 0) / items.length) : "—"; })()}
          </p>
        </div>
        <div className="stat-card">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Equipped</span>
          <p className="text-base font-bold font-mono">{gear.filter((g) => g.item_id > 0).length}/{gear.length}</p>
        </div>
      </div>

      {/* Gear Issues from Data Analysis */}
      {data?.gear_issues_analysis && data.gear_issues_analysis.length > 0 && (() => {
        const realIssues = data.gear_issues_analysis.filter(issue => {
          const lower = issue.toLowerCase();
          return lower.includes("[no enchant]") || lower.includes("[no gem]") ||
                 lower.includes("[common gem]") || lower.includes("[meta gem inactive");
        });
        return realIssues.length > 0 ? (
          <div className="mt-4" data-testid="gear-issues-analysis">
            <h4 className="text-xs font-mono uppercase tracking-wider text-destructive mb-2">
              Gear Issues (from Data Analysis) — {realIssues.length} issue(s)
            </h4>
            <div className="space-y-1">
              {realIssues.map((issue, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-destructive/5 border border-destructive/10 rounded-sm">
                  <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-foreground/80">{issue}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );
}

function SummaryCard({ label, good, total }) {
  const pct = total > 0 ? Math.round((good / total) * 100) : 0;
  const color = pct === 100 ? "text-green-500" : pct >= 50 ? "text-yellow-500" : total === 0 ? "text-muted-foreground" : "text-destructive";
  return (
    <div className="stat-card" data-testid={`gear-summary-${label.toLowerCase()}`}>
      <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <p className={`text-base font-bold font-mono ${color}`}>{good}/{total} <span className="text-sm">({pct}%)</span></p>
    </div>
  );
}
